import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { SHIPPING_AGENCY_TRANSFER_STATUS } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/utils";
import { VESSEL_CALL_RESTRICTED_STATUSES } from "@features/vessel-call/vessel-call-status/utils";
import type { CreatePortServicePayload } from "@services/port-services/types";
import { submitPortServiceRequest } from "@services/portServicesApi";
import { getLatestShippingAgencyTransfer } from "@services/shippingAgencyTransferApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { paths } from "@utils/navigation";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import type { z } from "zod";
import { getPortServiceFlow } from "../flow/waste-disposal/utils";
import {
  hasOperationsApproved,
  isShipChandlingLifeboatsService,
  SHIP_TO_SHIP,
} from "../form/utils";
import { generatePrefixedNotifyList } from "../preview/utils";
import { getPortServicesFormSchema } from "../schema/portServicesFormSchema";
import { usePortServicesFormStore } from "../store/usePortServicesForm.store";

type PortServicesFormValues = z.infer<ReturnType<typeof getPortServicesFormSchema>>;

type PortServiceError = {
  message?: string;
  response?: {
    data?: {
      detail?: string;
    };
  };
};

interface UsePortServiceProps {
  getFormValues: () => PortServicesFormValues;
  resetForm: () => void;
  vesselCallId: string;
  vesselCallStatus: string;
  notifyList: string[];
  isThirdShippingAgent?: boolean;
  isOwnersMatter?: boolean;
}
const LANG_BASE = "vessel-call.port-services";
const LANG_BASE_TRANSFER = "vessel-call.shipping-agency-transfer.notifications";
const BUNKERING_CATEGORY = "Bunkering";
const DEBUNKERING_CATEGORY = "De-bunkering";
const DE_SLOPING_CATEGORY = "De-sloping";
const SHIP_TO_SHIP_CATEGORY = "Ship to Ship";
const SHIP_TO_SHIP_OTHER_VESSEL_ROLE = SHIP_TO_SHIP?.OTHER_VESSEL_ROLE ?? "SECONDARY";

const parseNumericValue = (value?: string) => {
  if (!value?.trim()) {
    return undefined;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? undefined : parsedValue;
};

export interface UsePortServiceResult {
  onSubmit: () => Promise<void>;
  isSubmitting: boolean;
}

export const useSubmitPortService = (props: UsePortServiceProps): UsePortServiceResult => {
  const {
    resetForm,
    getFormValues,
    vesselCallId,
    vesselCallStatus,
    notifyList,
    isThirdShippingAgent,
    isOwnersMatter,
  } = props;

  const navigate = useNavigate();
  const clearUploadStore = useUploadStore((state) => state.clearAll);
  const { notify } = useNotification();

  const { data: latestTransfer } = useQuery({
    queryKey: ["getLatestShippingAgencyTransfer", vesselCallId],
    queryFn: () =>
      getLatestShippingAgencyTransfer("bff-vessel-call", {
        vesselCallId: vesselCallId ?? "",
      }),
    enabled: !!vesselCallId && (isOwnersMatter || !isThirdShippingAgent),
    staleTime: 0,
    refetchOnMount: true,
  });

  const portService = useMutation<unknown, PortServiceError, CreatePortServicePayload>({
    mutationFn: (payload) => {
      return runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceRequest",
          action: "submit_port_service",
          entityType: "port_service",
        },
        (transactionId) =>
          submitPortServiceRequest({
            service: "bff-vessel-call",
            portServicePayload: payload,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: () => {
      resetForm();
      navigate(
        `/${paths.vesselCall.index}/${paths.vesselCall.vesselCallPreview}/${vesselCallId}/${paths.vesselCall.portServices}`,
      );
      notify({
        title: t(`${LANG_BASE}.notifications.service-added-success-title`),
        description: t(`${LANG_BASE}.notifications.service-added-success-description`),
        type: "success",
      });

      clearUploadStore();
    },
    onError: (error: PortServiceError) => {
      notify({
        title: t(`${LANG_BASE}.notifications.service-added-error-title`),
        description:
          error?.response?.data?.detail ??
          t(`${LANG_BASE}.notifications.service-added-error-description`),
        type: "error",
      });
      console.error("Error submitting port service request:", error);
    },
  });

  const onSubmit = async (): Promise<void> => {
    if (portService.isPending) {
      return;
    }

    const formValues = getFormValues();
    const hasPendingTransfer =
      latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_APPROVAL ||
      latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_MARINE_DEPARTMENT_APPROVAL;

    const hasRestrictedVesselCallStatus = Object.values(VESSEL_CALL_RESTRICTED_STATUSES).some(
      (status) => status === vesselCallStatus,
    );

    if (hasRestrictedVesselCallStatus) {
      notify({
        title: t(`${LANG_BASE}.notifications.service-added-error-title`),
        description: t(`${LANG_BASE}.notifications.service-actions-not-allowed`),
        type: "error",
      });
      return;
    }

    const flow = getPortServiceFlow(
      formValues.serviceCategory,
      formValues?.serviceType || "",
      formValues?.serviceSubType || "",
    );

    const needsOperationsApproval =
      formValues?.serviceType === "Sludge" ? true : hasOperationsApproved(flow);

    if (hasPendingTransfer) {
      notify({
        title: t(`${LANG_BASE}.notifications.service-added-error-title`),
        description: t(`${LANG_BASE_TRANSFER}.active-shipping-agency-transfer-error`),
        type: "error",
      });
      return;
    } else {
      const {
        attachmentDocuments,
        billOfLadingDocuments,
        cargoManifestDocuments,
        certificateOfOriginDocuments,
        description,
        hoseCertificateDocuments,
        hoseInspectionDate,
        otherDocuments,
        otherVesselCallId,
        serviceCategory,
        serviceSubType,
        serviceType,
        sireReportDocuments,
        sireReportIssueDate,
        rts,
        quantity,
        unitOfMeasurement,
        numberOfSkips,
        garbageCategory,
        typeOfSludge,
        serviceLocation,
        bunkeringType,
        berthOrAnchorageCode,
        deSlopingType,
        bunkerBargeIMO,
        bunkeringCompanyName,
        deSlopingCompanyName,
        bunkerBargeVesselCallId,
        registeredAgentName,
        registeredAgentContactPerson,
        serviceProviderName,
        operationLocation,
        intendedOperationDetails,
        wasteResiduesForDischargeFacility,
        bunkerBargeName,
        direction,
        numberOfPackagesHandled,
        additionalResources,
        receivingVesselImoNumber,
        receivingVesselName,
        receivingVesselCallId,
        customsDocuments,
        remarks,
        garbageDocument,
        slopsDocument,
        sludgeDocument,
        debunkeringDocument,
      } = formValues;
      const { serviceCategoryCode, serviceTypeCode, serviceSubTypeCode, addWasteDraft } =
        usePortServicesFormStore.getState();

      const isShipChandlingLifeboats = isShipChandlingLifeboatsService(
        serviceCategory,
        serviceType,
      );

      const normalizeDocumentDate = (value?: string | Date | null) => {
        if (!value) {
          return null;
        }

        const date = new Date(value);

        return Number.isNaN(date.getTime()) ? null : date.toISOString().slice(0, 10);
      };

      const normalizeStsDocs = (
        docs?: typeof attachmentDocuments,
        fallbackType?: string,
        extraDateFieldName?: "issueDate" | "inspectionDate",
        extraDateValue?: string | Date | null,
      ) =>
        docs?.map((doc, idx) => {
          const normalizedExpirationDate = normalizeDocumentDate(doc.expirationDate);
          const normalizedExtraDate = normalizeDocumentDate(extraDateValue);
          const resolvedDocumentType = doc.certificateType ?? doc.type ?? fallbackType ?? "";

          return {
            name: doc.name || `Document ${idx + 1}`,
            type: resolvedDocumentType,
            url: doc.url,
            originalFileName: doc.originalFileName ?? doc.name ?? `Document ${idx + 1}`,
            ...(extraDateFieldName
              ? normalizedExpirationDate
                ? { expirationDate: normalizedExpirationDate }
                : {}
              : { expirationDate: normalizedExpirationDate }),
            ...(extraDateFieldName && normalizedExtraDate
              ? { [extraDateFieldName]: normalizedExtraDate }
              : {}),
          };
        }) ?? [];

      const normalizedAttachmentDocs =
        attachmentDocuments?.map((doc, idx) => {
          const normalizedDocumentType = doc.documentType?.trim() ?? "";
          const shouldKeepSpecify = normalizedDocumentType.toUpperCase() === "OTHER_SPECIFY";

          return {
            name: doc.name || `Document ${idx + 1}`,
            type: normalizedDocumentType
              ? "ADDITIONAL_DOCUMENT"
              : (doc.type ?? doc.certificateType ?? "PORT_SERVICES"),
            ...(normalizedDocumentType ? { documentType: normalizedDocumentType } : {}),
            ...(shouldKeepSpecify ? { specify: doc.otherSpecifyType?.trim() ?? "" } : {}),
            ...(doc.documentSummary ? { documentSummary: doc.documentSummary } : {}),
            url: doc.url,
            originalFileName: doc.originalFileName ?? doc.name ?? `Document ${idx + 1}`,
            expirationDate: doc.expirationDate ?? null,
            issueDate: doc.issueDate ?? null,
            inspectionDate: doc.inspectionDate ?? null,
          };
        }) ?? [];

      const normalizeBusinessDocs = (
        docs?: typeof attachmentDocuments,
        fallbackType = "PORT_SERVICES",
      ) =>
        docs?.map((doc, idx) => ({
          name: doc.name || `Document ${idx + 1}`,
          type: doc.type ?? doc.certificateType ?? fallbackType,
          url: doc.url,
          originalFileName: doc.originalFileName ?? doc.name ?? `Document ${idx + 1}`,
          expirationDate: doc.expirationDate ?? null,
          issueDate: doc.issueDate ?? null,
          inspectionDate: doc.inspectionDate ?? null,
        })) ?? [];

      const normalizedBusinessDocs = [
        ...normalizeBusinessDocs(garbageDocument),
        ...normalizeBusinessDocs(slopsDocument, "OILY_WASTE_DELIVERY_FORM_ONSHORE"),
        ...normalizeBusinessDocs(sludgeDocument),
        ...normalizeBusinessDocs(debunkeringDocument, "BUNKER_DELIVERY_NOTE"),
      ];

      const normalizedShipChandlingDocs =
        customsDocuments?.map((doc) => ({
          name: "Customs Document",
          type: doc.type ?? doc.certificateType ?? "CUSTOMS_DOCUMENT",
          url: doc.url,
          originalFileName: doc.originalFileName ?? doc.name ?? "",
          expirationDate: doc.expirationDate ?? null,
          issueDate: doc.issueDate ?? null,
          inspectionDate: doc.inspectionDate ?? null,
        })) ?? [];

      const isBunkerBargeRequest =
        serviceCategory === BUNKERING_CATEGORY ||
        serviceCategory === DEBUNKERING_CATEGORY ||
        serviceCategory === DE_SLOPING_CATEGORY;

      const isShipToShipRequest =
        serviceCategory === (SHIP_TO_SHIP?.LABEL ?? SHIP_TO_SHIP_CATEGORY);

      const hasShipToShipContext = Boolean(
        otherVesselCallId ||
          sireReportIssueDate ||
          hoseInspectionDate ||
          sireReportDocuments?.length ||
          billOfLadingDocuments?.length ||
          cargoManifestDocuments?.length ||
          certificateOfOriginDocuments?.length ||
          hoseCertificateDocuments?.length ||
          otherDocuments?.length,
      );

      const normalizedDocs = isShipChandlingLifeboats
        ? [...normalizedShipChandlingDocs, ...normalizedAttachmentDocs]
        : isShipToShipRequest
          ? [
              ...normalizeStsDocs(
                sireReportDocuments,
                "SIRE_REPORT",
                "issueDate",
                sireReportIssueDate,
              ),
              ...normalizeStsDocs(billOfLadingDocuments, "BILL_OF_LADING"),
              ...normalizeStsDocs(cargoManifestDocuments, "CARGO_MANIFEST"),
              ...normalizeStsDocs(certificateOfOriginDocuments, "CERTIFICATE_OF_ORIGIN"),
              ...normalizeStsDocs(
                hoseCertificateDocuments,
                "HOSE_CERTIFICATE",
                "inspectionDate",
                hoseInspectionDate,
              ),
              ...normalizeStsDocs(otherDocuments, "OTHER_DOCUMENTS"),
              ...normalizedAttachmentDocs,
            ]
          : [...normalizedBusinessDocs, ...normalizedAttachmentDocs];

      const notificationFor = generatePrefixedNotifyList("CREATE", notifyList);
      const linkedVesselCalls = [
        {
          vesselCallId,
          role: "MAIN",
        },
      ];

      if (
        isBunkerBargeRequest &&
        bunkerBargeVesselCallId &&
        bunkerBargeVesselCallId !== vesselCallId
      ) {
        linkedVesselCalls.push({
          vesselCallId: bunkerBargeVesselCallId,
          role: "SECONDARY",
        });
      }

      if (isShipToShipRequest && otherVesselCallId && otherVesselCallId !== vesselCallId) {
        linkedVesselCalls.push({
          vesselCallId: otherVesselCallId,
          role: SHIP_TO_SHIP_OTHER_VESSEL_ROLE,
        });
      }

      const payload = {
        linkedVesselCalls,
        status: "PENDING_APPROVAL",
        documents: normalizedDocs.length > 0 ? normalizedDocs : undefined,
        description,
        remarks: remarks ?? "",
        serviceCategory,
        serviceSubType,
        serviceType,
        ...(hasShipToShipContext
          ? {
              sireReportIssueDate: sireReportIssueDate || null,
              hoseInspectionDate: hoseInspectionDate || null,
            }
          : {}),
        rts: rts || null,
        etc: null,
        ets: null,
        quantity,
        unitOfMeasurement,
        numberOfSkips,
        garbageCategory,
        typeOfSludge,
        location: serviceLocation,
        registeredAgentName: registeredAgentName ?? "",
        registeredAgentContactPerson: registeredAgentContactPerson ?? "",
        serviceProviderName: serviceProviderName ?? "",
        operationLocation: operationLocation ?? "",
        intendedOperationDetails: intendedOperationDetails ?? "",
        wasteResiduesForDischargeFacility,
        addWasteDraft,
        notificationFor,
        serviceCategoryCode,
        serviceTypeCode,
        serviceSubTypeCode,
        bunkerBargeIMO,
        bunkeringType,
        ...(serviceCategory === "Bunkering" ? { berthOrAnchorageCode } : {}),
        bunkeringCompanyName,
        deSlopingType,
        deSlopingCompanyName,
        needsOperationsApproval,
        bunkerBargeName,
        ...(isShipChandlingLifeboats
          ? {
              shipChandlingDirection: direction?.toUpperCase(),
              numberOfPackagesHandled: parseNumericValue(numberOfPackagesHandled),
              resourcesAndEquipment:
                additionalResources?.map((resource) => ({
                  resourceTypeCode: null,
                  resourceTypeName: resource.resourceTypeLabel || resource.resourceType,
                  numberOfResources: parseNumericValue(resource.numberOfResources),
                })) ?? [],
              receivingVesselImo: parseNumericValue(receivingVesselImoNumber),
              receivingVesselName: receivingVesselName ?? "",
              receivingVesselCallId: receivingVesselCallId || undefined,
            }
          : {}),
      };
      portService.mutate(payload);
    }
  };
  return { onSubmit, isSubmitting: portService.isPending };
};
